import type { QuantityParsingError } from ".";
import { QUANTITY_PARSING_ERROR } from ".";
import t from "../../../../i18n";
import { FILE_PARSING_ERROR } from "../../common/file";
import type { ParsingErrorMessageMap } from "../../types";

/**
 * @description Error messages that can be presented to the user by the quantity parser.
 */
export const QuantityParsingErrorMessages: ParsingErrorMessageMap<
  QUANTITY_PARSING_ERROR | FILE_PARSING_ERROR
> = {
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
  [FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED]: () =>
    t("setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded"),
  [FILE_PARSING_ERROR.FILE_INVALID_TYPE]: () =>
    t("setup.diagnosis.observable.import_quantities.error.invalid_format"),
  [QUANTITY_PARSING_ERROR.DATA_FILE_EMPTY]: () => t("setup.quantity.import.error.data_file_empty"),
  [QUANTITY_PARSING_ERROR.MISSING_COLUMNS]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantities.import.error.missing_columns_lithium")
      : t("setup.quantities.import.error.missing_columns_helium"),
  [QUANTITY_PARSING_ERROR.DATA_FILE_QUANTITY_NAME_TOO_LONG]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantity.import.error.data_quantity_name_too_long_lithium", errorData.value || "")
      : t("setup.quantity.import.error.data_quantity_name_too_long_helium", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_PAIR_VALUE_FORMULA_SENSOR]: errorData =>
    errorData.key === "lithium"
      ? t(
          "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_lithium",
          errorData.value || "",
        )
      : t(
          "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_helium",
          errorData.value || "",
        ),
  // [QUANTITY_PARSING_ERROR.DATA_QUANTITY_DUPLICATED]: errorData =>
  //   t("setup.quantity.import.error.data_quantity_duplicated", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_MIN_MAX_STD_INVALID]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantity.import.error.data_min_max_std_invalid_lithium", errorData.value || "")
      : t("setup.quantity.import.error.data_min_max_std_invalid_helium", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_MIN_SUP_MAX_INVALID]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantity.import.error.data_min_sup_max_invalid_lithium", errorData.value || "")
      : t("setup.quantity.import.error.data_min_sup_max_invalid_helium", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_NEGATIVE_MAXSTD_INVALID]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantity.import.error.data_negative_maxStd_invalid_lithium", errorData.value || "")
      : t("setup.quantity.import.error.data_negative_maxStd_invalid_helium", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_MAXSTD_WITH_FORMULA_INVALID]: errorData =>
    errorData.key === "lithium"
      ? t(
          "setup.quantity.import.error.data_maxStd_with_formula_invalid_lithium",
          errorData.value || "",
        )
      : t(
          "setup.quantity.import.error.data_maxStd_with_formula_invalid_helium",
          errorData.value || "",
        ),
  [QUANTITY_PARSING_ERROR.DATA_FILE_DESCRIPTION_TOO_LONG]: errorData =>
    t("setup.quantity.import.error.data_description_too_long", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FILE_UNIT_TOO_LONG]: errorData =>
    t("setup.quantity.import.error.data_unit_too_long", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FILE_SENSOR_TAG_TOO_LONG]: errorData =>
    t("setup.quantity.import.error.data_sensor_tag_too_long", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FILE_FORMULA_TOO_LONG]: errorData =>
    t("setup.quantity.import.error.data_formula_too_long", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_DUPLICATE_STARTS_AT]: errorData =>
    t(
      "setup.quantity.import.error.data_formula_version_duplicate_startsAt",
      errorData.value || "",
      errorData.hint || "",
    ),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_EMPTY_FORMULA]: errorData =>
    t(
      "setup.quantity.import.error.data_formula_version_empty_formula",
      errorData.value || "",
      errorData.hint || "",
    ),
  [QUANTITY_PARSING_ERROR.DATA_START_DATE_MISSING_BASE]: errorData =>
    t("setup.quantity.import.error.data_start_date_missing_base", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_BASE_FORMULA_REQUIRED]: errorData =>
    t(
      "setup.quantity.import.error.data_formula_version_base_formula_required",
      errorData.value || "",
    ),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_MULTIPLE_BASE_ROWS]: errorData =>
    t("setup.quantity.import.error.data_formula_version_multiple_base_rows", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_BASE_SENSOR_TAG_NOT_ALLOWED]: errorData =>
    t(
      "setup.quantity.import.error.data_formula_version_base_sensor_tag_not_allowed",
      errorData.value || "",
    ),
  [QUANTITY_PARSING_ERROR.DATA_START_DATE_INVALID_FORMAT]: errorData =>
    t("setup.quantity.import.error.data_start_date_invalid_format", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_UNIT_MISMATCH]: errorData =>
    t("setup.quantity.import.error.data_formula_version_unit_mismatch", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_SENSOR_TAG_MISMATCH]: errorData =>
    t(
      "setup.quantity.import.error.data_formula_version_sensor_tag_mismatch",
      errorData.value || "",
    ),
  [QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_METADATA_MISMATCH]: errorData =>
    t("setup.quantity.import.error.data_formula_version_metadata_mismatch", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_EQUIPMENTS]: errorData =>
    errorData.key === "lithium"
      ? t("setup.quantity.import.error.data_invalid_equipments_lithium", errorData.value || "")
      : t("setup.quantity.import.error.data_invalid_equipments_helium", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_QUANTITY_NAME_FORMAT]: errorData =>
    errorData.key === "lithium"
      ? t(
          "setup.quantity.import.error.data_invalid_quantity_name_format_lithium",
          errorData.value || "",
        )
      : t(
          "setup.quantity.import.error.data_invalid_quantity_name_format_helium",
          errorData.value || "",
        ),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_MISSING_SENSOR_TAG_OR_FORMULA]: errorData =>
    t("setup.quantity.import.error.missing_sensortag_or_formula", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_LOCKED_FIELD]: errorData =>
    t("setup.quantity.import.error.data_invalid_locked_field", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_INVALID_HIDDEN_FIELD]: errorData =>
    t("setup.quantity.import.error.data_invalid_hidden_field", errorData.value || ""),
  [QUANTITY_PARSING_ERROR.DATA_LOCKED_TRUE_WITHOUT_FORMULA]: errorData =>
    t("setup.quantity.import.error.locked_true_without_formula", errorData.value || ""),
};

/**
 * @description Get error message from error kind.
 * @param error QuantityParsingError
 * @returns Translated error message.
 */
export const getQuantityParsingErrorMessage = (error: QuantityParsingError) => {
  const errorMessage = QuantityParsingErrorMessages[error.kind];
  return errorMessage ? errorMessage(error.data) : "Unknown error";
};
